import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Loader2, Mail } from 'lucide-react';

import forgetPassword from '../api/users/forgetPassword';
import { I18nContext } from '../context/translations/TranslationsContext.context';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle
} from '../components/ui/card';
import { toast } from '../components/ui/sonner';

const ForgetPasswordV2 = () => {
	const {
		state: { translate }
	} = useContext(I18nContext);

	const navigate = useNavigate();

	// Un único campo: NIF o correo, y lo decide la "@" dentro de `forgetPassword`.
	const [identifier, setIdentifier] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [identifierError, setIdentifierError] = useState(false);
	const [recoverySent, setRecoverySent] = useState(false);

	const handleRecoverPassword = async (event: React.FormEvent) => {
		event.preventDefault();

		if (!identifier) {
			setIdentifierError(true);
			toast.error(translate('forgotPassword.identifierRequired'));
			return;
		}

		setIdentifierError(false);
		setIsLoading(true);

		try {
			const response = await forgetPassword(identifier);

			// La respuesta es idéntica exista o no la cuenta: la pantalla nunca confirma
			// ni desmiente que alguien esté en el sistema. A quien no le llegue nada, la
			// propia confirmación le dice qué más probar.
			if (response.success) {
				setRecoverySent(true);
				toast.success(translate('forgotPassword.emailSent'));
			} else {
				toast.error(translate('forgotPassword.recoveryError'));
			}
		} catch (error) {
			console.error('Error:', error);
			toast.error(translate('forgotPassword.recoveryProblem'));
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
			<Card className="w-full max-w-md shadow-lg">
				<CardHeader className="text-center space-y-4">
					<div className="flex justify-center">
						<img src="/imgs/LOGO_VERDE_2.svg" alt="Logo Arca" className="h-12" />
					</div>
					<CardTitle className="text-2xl font-semibold text-gray-900">
						{translate('forgotPassword.title') || 'Recuperar contraseña'}
					</CardTitle>
					<CardDescription className="text-gray-600">
						{recoverySent
							? translate('forgotPassword.checkEmail')
							: translate('forgotPassword.subtitleByNifOrEmail')}
					</CardDescription>
				</CardHeader>

				{!recoverySent ? (
					<form onSubmit={handleRecoverPassword}>
						<CardContent className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="identifier" className="text-sm font-medium text-gray-700">
									{translate('general.login.nifOrEmailLabel')}
								</Label>
								<div className="relative">
									{/*
									 * `type="text"` para que quepa un NIF, y la autocapitalización
									 * apagada a mano porque `type="email"` la traía de serie: si no,
									 * el móvil manda `Anon@…` y el correo no resuelve.
									 */}
									<Input
										id="identifier"
										name="identifier"
										type="text"
										autoCapitalize="none"
										autoCorrect="off"
										spellCheck={false}
										autoComplete="username"
										// Sin `required` a propósito: con él, el navegador bloquea el envío con su
										// propia burbuja y el evento `submit` no se dispara, así que el mensaje de
										// abajo —con `role="alert"`, que un lector de pantalla sí anuncia— no
										// llegaría a pintarse nunca. La obligatoriedad la impone
										// `handleRecoverPassword`.
										aria-invalid={identifierError}
										aria-describedby={identifierError ? 'identifier-error' : undefined}
										value={identifier}
										onChange={(e) => {
											setIdentifier(e.target.value.trim());
											if (identifierError) setIdentifierError(false);
										}}
										placeholder="00000000T"
										className={`h-11 pl-10 ${identifierError ? 'border-red-500' : ''}`}
									/>
									<Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
								</div>
								{identifierError && (
									<p id="identifier-error" role="alert" className="text-sm text-red-500">
										{translate('forgotPassword.identifierRequired')}
									</p>
								)}
							</div>
						</CardContent>

						<CardFooter className="flex flex-col gap-4">
							<Button
								type="submit"
								className="w-full h-11 bg-arca-dark-green hover:bg-arca-dark-green/90 text-white font-medium"
								disabled={isLoading}
							>
								{isLoading ? (
									<>
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										{translate('general.loading') || 'Enviando...'}
									</>
								) : (
									translate('general.login.recoverPassword') || 'Enviar enlace de recuperación'
								)}
							</Button>

							<button
								type="button"
								onClick={() => navigate('/login')}
								className="flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-arca-dark-green transition-colors"
							>
								<ArrowLeft className="h-4 w-4" />
								{translate('general.login.haveAccount') || 'Volver al inicio de sesión'}
							</button>
						</CardFooter>
					</form>
				) : (
					<CardFooter className="flex flex-col gap-4 pt-0">
						<div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 rounded-full">
							<Mail className="h-8 w-8 text-arca-dark-green" />
						</div>
						{/*
						 * Mensaje genérico a propósito: por correo no se confirma ni se
						 * desmiente que la cuenta exista, y por NIF no sabemos a qué
						 * dirección ha salido (el backend elige entre corporativo y personal).
						 */}
						<p className="text-center text-sm text-gray-600">
							{translate('forgotPassword.sentGeneric')}
						</p>
						{/*
						 * 🔴 La salida de quien descubre AQUÍ que no puede recuperarse: pidió
						 * el enlace y no le llega porque su correo no es el que tenemos, o su
						 * NIF no figura en su ficha. Antes le quedaba el flujo viejo de «te
						 * mandamos una contraseña»; desde que se retiró, esto es lo único.
						 */}
						<button
							type="button"
							onClick={() => navigate('/recover-access')}
							className="text-sm text-gray-600 underline hover:text-arca-dark-green"
						>
							{translate('general.login.recoverAccessCta')}
						</button>
						<Button
							type="button"
							variant="outline"
							className="w-full h-11"
							onClick={() => navigate('/login')}
						>
							<ArrowLeft className="mr-2 h-4 w-4" />
							{translate('general.login.haveAccount') || 'Volver al inicio de sesión'}
						</Button>
					</CardFooter>
				)}
			</Card>
		</div>
	);
};

export default ForgetPasswordV2;
